import { cls } from "app/utils";
import React, { useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";

interface DropdownProps {
  children?: React.ReactNode;
  btnContent: React.ReactNode;
  btnContentBadge?: React.ReactNode;
  btnClassName?: string;
  dropdownClassname?: string;
  dropdownWrapperClass?: string;
  hideButtonStyles?: boolean;
  closeOnClick?: boolean;
  closeModal?: any;
  onOpen?: () => void;
  /**
   * CFTB-3428: added to the trigger while the dropdown is open. The open state is
   * this component's own, so a caller wanting to turn its arrow over — or mark the
   * trigger any other way — has no way to know without it. Opt-in, so the callers
   * that pass nothing are untouched.
   */
  btnOpenClassName?: string;
}

const DropdownButton = ({
  children,
  btnContent,
  btnContentBadge,
  btnClassName,
  dropdownClassname,
  dropdownWrapperClass,
  hideButtonStyles,
  closeOnClick = false,
  closeModal,
  onOpen,
  btnOpenClassName,
}: DropdownProps): React.ReactElement => {
  const ref = useRef(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleClickOutside = (e: any): void => {
    if (isOpen && ref.current && !ref.current.contains(e.target)) {
      if (
        !ref.current.contains(e.target) &&
        !e.target.closest(`.${styles.dropdown_container}`)
      ) {
        setIsOpen(false);
      }
      if (closeOnClick) {
        setIsOpen(false);
      }
    }
  };

  // A keyboard drives this as well as the mouse now, so the event is either kind.
  const toggleDropDown = (
    evt: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
  ) => {
    evt.preventDefault();
    setIsOpen(!isOpen);
    if (onOpen) {
      onOpen();
    }
  };

  useEffect((): void => {
    setIsOpen(false);
  }, [closeModal]);

  useEffect((): (() => void) => {
    document.addEventListener("click", handleClickOutside, true);
    return (): void => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [isOpen]);

  return (
    <div className={cls(styles.container, dropdownClassname)}>
      {btnContentBadge && (
        <div className={styles.badge_container}>{btnContentBadge}</div>
      )}
      {/*
        A div rather than a button because callers put buttons of their own in
        `btnContent`, and a button inside a button is invalid — but it carries
        `aria-expanded`, so it has to answer to a keyboard like the control it
        claims to be: reachable by Tab, announced as a button, and opened with
        Enter or Space the way one is.
      */}
      <div
        ref={ref}
        onClick={toggleDropDown}
        onKeyDown={(e) => {
          // Only keys pressed on the trigger itself: a Space typed in an input
          // inside `btnContent` (UserSelect's search) bubbles up here, and
          // swallowing it made spaces impossible to type (CN-2837).
          if (e.target !== e.currentTarget) {
            return;
          }
          if (e.key === "Enter" || e.key === " ") {
            // Space scrolls the page otherwise, and Enter can submit a form
            // this trigger happens to sit in.
            toggleDropDown(e);
          }
        }}
        className={cls(styles.dropdown_btn, btnClassName, {
          ["btn btn-outline-secondary"]: !hideButtonStyles,
          [btnOpenClassName || ""]: Boolean(btnOpenClassName) && isOpen,
        })}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
      >
        {btnContent}
      </div>
      {/* {isOpen && ( */}
      {children && (
        <div
          className={cls(styles.dropdown_container, dropdownWrapperClass, {
            [styles.open]: isOpen,
            [styles.close]: !isOpen,
          })}
        >
          {children}
        </div>
      )}
      {/* )} */}
    </div>
  );
};

export default DropdownButton;
